import { useState, useRef, useEffect, ImgHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

interface OptimizedImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'onLoad'> {
  src: string;
  alt: string;
  /** Low-quality placeholder image or base64 blur data URL */
  placeholder?: string;
  /** CSS class for the container */
  containerClassName?: string;
  /** Whether to use native lazy loading (default: true) */
  lazy?: boolean;
  /** Blur amount for placeholder in pixels (default: 20) */
  blurAmount?: number;
  /** Fetch priority for LCP optimization ('high' | 'low' | 'auto') */
  fetchPriority?: 'high' | 'low' | 'auto';
  /** Aspect ratio to reserve space and prevent CLS (e.g., "16/9", "4/3", "1/1") */
  aspectRatio?: string;
}

/**
 * OptimizedImage - Image component with lazy loading and blur-up effect
 * Improves LCP by deferring off-screen images and providing visual feedback during load
 * Prevents CLS with optional aspect ratio reservation
 */
export function OptimizedImage({
  src,
  alt,
  placeholder,
  containerClassName,
  className,
  lazy = true,
  blurAmount = 20,
  fetchPriority,
  aspectRatio,
  ...props
}: OptimizedImageProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(!lazy);
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!lazy) return;

    const element = containerRef.current;
    if (!element) return;

    // Check if already in viewport
    const rect = element.getBoundingClientRect();
    if (rect.top < window.innerHeight + 100) {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '100px',
        threshold: 0
      }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [lazy]);

  // Handle image load
  const handleLoad = () => {
    setIsLoaded(true);
  };

  // Generate a simple gradient placeholder if none provided
  const defaultPlaceholder = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"%3E%3Crect fill="%23e5e7eb" width="1" height="1"/%3E%3C/svg%3E';
  const placeholderSrc = placeholder || defaultPlaceholder;

  // Container style with optional aspect ratio for CLS prevention
  const containerStyle = aspectRatio ? { aspectRatio } : undefined;

  return (
    <div 
      ref={containerRef}
      className={cn('relative overflow-hidden', containerClassName)}
      style={containerStyle}
    >
      {/* Placeholder layer with blur */}
      <div
        className={cn(
          'absolute inset-0 transition-opacity duration-500',
          isLoaded ? 'opacity-0' : 'opacity-100'
        )}
        style={{
          backgroundImage: `url(${placeholderSrc})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: `blur(${blurAmount}px)`,
          transform: 'scale(1.1)', // Prevent blur edges from showing
        }}
        aria-hidden="true"
      />

      {/* Actual image */}
      {isInView && (
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          loading={lazy ? 'lazy' : 'eager'}
          decoding="async"
          // @ts-expect-error - fetchPriority is a valid attribute but not yet in React types
          fetchpriority={fetchPriority}
          onLoad={handleLoad}
          className={cn(
            'transition-opacity duration-500',
            isLoaded ? 'opacity-100' : 'opacity-0',
            className
          )}
          {...props}
        />
      )}
    </div>
  );
}

export default OptimizedImage;
